import { useEffect, useState } from 'react';
import { Send, RefreshCw, LogOut, Power, MessageCircle, Smartphone, Users, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../services/apiService';

type WhatsAppStatus = {
  ready: boolean;
  initializing: boolean;
  hasQr: boolean;
  account: string | null;
  phoneNumber: string | null;
  testMod: boolean;
  hedef: string;
  selectedGroupId?: string | null;
  lastError?: string | null;
  lastQrAt?: string | null;
};

type WhatsAppGroup = {
  id: string;
  name: string;
};

export function WhatsappWebPanel() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(true);
  const [responsePreview, setResponsePreview] = useState<any>(null);
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const fetchStatus = async () => {
    try {
      const res: any = await apiService.get('/whatsapp-web/status');
      if (res?.success) {
        setStatus(res.data);
      }
    } catch (error: any) {
      console.error('WhatsApp Web status fetch error:', error);
    }
  };

  const fetchQr = async () => {
    try {
      const res: any = await apiService.get('/whatsapp-web/qr');
      if (res?.success && res?.data) {
        setQrDataUrl(res.data);
      } else {
        setQrDataUrl(null);
      }
    } catch (error: any) {
      console.error('WhatsApp Web QR fetch error:', error);
      setQrDataUrl(null);
    }
  };

  const fetchGroups = async () => {
    try {
      const res: any = await apiService.get('/whatsapp-web/groups');
      if (res?.success) {
        const items = Array.isArray(res.data) ? res.data : [];
        setGroups(items);
      }
    } catch (error: any) {
      console.error('WhatsApp Web groups fetch error:', error);
      setGroups([]);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchQr();
  }, []);

  useEffect(() => {
    if (status?.ready) {
      fetchGroups();
      setSelectedGroupId(status.selectedGroupId || '');
    }
  }, [status?.ready, status?.selectedGroupId]);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(() => {
      fetchStatus();
      fetchQr();
    }, 5000);
    return () => clearInterval(interval);
  }, [polling]);

  const handleInitialize = async () => {
    try {
      setLoading(true);
      const res: any = await apiService.post('/whatsapp-web/initialize');
      if (res?.success) {
        toast.success(res.message || 'Başlatma komutu verildi');
      } else {
        toast.error(res?.message || 'Başlatılamadı');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Başlatma başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      const res: any = await apiService.post('/whatsapp-web/logout');
      if (res?.success) {
        toast.success(res.message || 'Oturum kapatıldı');
      } else {
        toast.error(res?.message || 'Çıkış yapılamadı');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Çıkış başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!phoneNumber.trim() || !message.trim()) {
      toast.error('Telefon numarası ve mesaj zorunlu');
      return;
    }

    try {
      setLoading(true);
      const res: any = await apiService.post('/whatsapp-web/send', { phoneNumber, message });
      setResponsePreview(res);
      if (res?.success) {
        toast.success(`${res.sent || 0} numaraya gönderildi`);
      } else {
        toast.error(res?.message || 'Mesaj gönderilemedi');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Gönderme başarısız');
      setResponsePreview(error?.response?.data || null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGroup = async (groupId: string) => {
    setSelectedGroupId(groupId);
    if (!groupId) return;
    try {
      const res: any = await apiService.post('/whatsapp-web/select-group', { groupId });
      if (res?.success) {
        toast.success(res.message || 'Hedef grup güncellendi');
        await fetchStatus();
      }
    } catch (error: any) {
      toast.error(error?.message || 'Grup seçilemedi');
    }
  };

  const handleSendToGroup = async () => {
    if (!selectedGroupId || !message.trim()) {
      toast.error('Grup ve mesaj zorunlu');
      return;
    }
    try {
      setLoading(true);
      const res: any = await apiService.post('/whatsapp-web/send-group', { groupId: selectedGroupId, message });
      setResponsePreview(res);
      if (res?.success) {
        toast.success('Gruba mesaj gönderildi');
      } else {
        toast.error(res?.message || 'Grup mesajı gönderilemedi');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Grup mesajı gönderilemedi');
      setResponsePreview(error?.response?.data || null);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = status?.ready ? 'bg-emerald-500' : status?.hasQr ? 'bg-amber-500' : 'bg-red-500';
  const statusText = status?.ready ? 'BAĞLI' : status?.hasQr ? 'QR BEKLENİYOR' : status?.initializing ? 'BAŞLATILIYOR' : 'BAĞLI DEĞİL';

  return (
    <div className="w-full p-6 space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-green-100 text-green-700 flex items-center justify-center">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WhatsApp Web</h1>
              <p className="text-sm text-gray-500">QR kod ile oturum açın, mesaj gönderin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white ${statusColor}`}>
              {status?.ready ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {statusText}
            </span>
            <button onClick={() => { fetchStatus(); fetchQr(); }} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50" title="Yenile">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Hesap</div>
          <div className="mt-2 text-lg font-semibold text-gray-900">{status?.account || '-'}</div>
          <div className="text-xs text-gray-400 mt-1">{status?.phoneNumber ? `+${status.phoneNumber}` : ''}</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Hedef</div>
          <div className="mt-2 text-lg font-semibold text-gray-900 flex items-center gap-2">
            {status?.testMod ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <Users className="w-4 h-4 text-blue-500" />}
            {status?.hedef || '-'}
          </div>
          {status?.testMod && <div className="text-xs text-amber-600 mt-1">Test modu aktif</div>}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">İşlem</div>
          <div className="mt-2 flex items-center gap-2">
            {!status?.ready ? (
              <button onClick={handleInitialize} disabled={loading || status?.initializing} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                <Power className="w-4 h-4" />
                {status?.initializing ? 'Başlatılıyor...' : 'Başlat'}
              </button>
            ) : (
              <button onClick={handleLogout} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60">
                <LogOut className="w-4 h-4" />
                Çıkış Yap
              </button>
            )}
          </div>
        </div>
      </div>

      {!status?.ready && status?.lastError && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <div className="text-sm font-semibold text-red-800">WhatsApp Web Hatası</div>
          <div className="mt-2 text-sm text-red-700 break-words">{status.lastError}</div>
          <div className="mt-3 text-xs text-red-600">
            Uretimde sik nedenler: Chromium yolu eksik, sunucuda sandbox/kutuphane problemi veya yazilabilir auth klasoru olmamasi.
          </div>
        </div>
      )}

      {!status?.ready && qrDataUrl && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">QR Kodu Tarayın</h2>
          <p className="text-sm text-gray-500 mb-4">WhatsApp mobil uygulamasından QR kodu tarayarak oturum açın</p>
          <img src={qrDataUrl} alt="WhatsApp QR" className="mx-auto w-64 h-64 rounded-xl border-4 border-white shadow-lg" />
          <p className="text-xs text-gray-400 mt-4">Sayfa 5 saniyede bir otomatik yenilenir{status?.lastQrAt ? ` • Son QR: ${new Date(status.lastQrAt).toLocaleString('tr-TR')}` : ''}</p>
        </div>
      )}

      {!status?.ready && !qrDataUrl && !status?.initializing && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-800">
          QR kod su an mevcut degil. "Baslat" butonuna bastiktan sonra hala gelmiyorsa yukaridaki hata mesaji uretim ortamini isaret ediyor olabilir.
        </div>
      )}

      {status?.ready && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-green-600" />
            Mesaj Gönder
          </h2>
          {!status?.testMod && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Grup Seçimi</label>
              <select value={selectedGroupId} onChange={(e) => void handleSelectGroup(e.target.value)} className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Grup Seçiniz</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
              </select>
              <p className="mt-2 text-xs text-gray-400">Bağlı WhatsApp hesabındaki gruplar listelenir ve seçilen grup varsayılan hedef olur.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası</label>
              <div className="relative">
                <Smartphone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="905XXXXXXXXX veya virgülle ayırın"
                  className="w-full rounded-xl border border-gray-300 pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Birden fazla numara için virgül kullanın: 905321234567,905441234567</p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-600">
              <div className="font-semibold text-gray-800 mb-2">Mesaj İşleme</div>
              <ul className="text-xs space-y-1 text-gray-500">
                <li>• TC kimlik no otomatik maskelenir</li>
                <li>• Link önizleme devre dışı bırakılır</li>
                <li>• Sürücü uygulama linki otomatik eklenir</li>
                <li>• Güvenlik belgeleri hatırlatması eklenir</li>
              </ul>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mesaj</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Gönderilecek WhatsApp mesajını yazın..."
              className="w-full min-h-[200px] rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={() => { setPhoneNumber(''); setMessage(''); setResponsePreview(null); }} className="px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50">Temizle</button>
            {!status?.testMod && (
              <button type="button" onClick={handleSendToGroup} disabled={loading || !selectedGroupId} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
                <Users className="w-4 h-4" />
                {loading ? 'Gönderiliyor...' : 'Gruba Gönder'}
              </button>
            )}
            <button type="button" onClick={handleSend} disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60">
              <Send className="w-4 h-4" />
              {loading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
            </button>
          </div>
        </div>
      )}

      {responsePreview && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="text-sm font-semibold text-gray-800 mb-3">API Yanıtı</div>
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded-xl p-4 overflow-auto whitespace-pre-wrap break-words">{JSON.stringify(responsePreview, null, 2)}</pre>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Webhook URL'leri</h2>
        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <code className="bg-gray-100 px-3 py-1 rounded-lg text-xs">POST /api/whatsapp-web/webhook/individual</code>
            <span className="text-gray-500">- Bireysel mesaj gönderimi (phoneNumber, message)</span>
          </div>
          <div className="flex items-start gap-3">
            <code className="bg-gray-100 px-3 py-1 rounded-lg text-xs">POST /api/whatsapp-web/webhook/teklif-topla</code>
            <span className="text-gray-500">- Teklif toplama webhook (buffer'lı grup mesajı)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
